import { arrayMove } from '@dnd-kit/sortable';
import { parseDate } from '@internationalized/date';
import { ColumnSort } from '@tanstack/react-table';
import { createContext } from 'react';
import { DateRange, DateValue } from 'react-aria-components';
import superjson from 'superjson';
import { createStore } from 'zustand';
import { devtools, persist, PersistStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { generateId } from '@/utils';

export type SortingFilter = ColumnSort & {
  hidden?: boolean;
  name?: string;
};

export type ColumnFilter = {
  id: string;
  type?: string;
};

export const DateRangeConditions = ['between'] as const;
export type DateRangeColumnFilter = ColumnFilter & {
  type?: 'date-range';
  condition?: (typeof DateRangeConditions)[number];
  value?: DateRange | null;
};

export const DateConditions = ['after', 'before'] as const;
export type DateColumnFilter = ColumnFilter & {
  type?: 'date';
  condition?: (typeof DateConditions)[number];
  value?: DateValue | null;
};

export const StringConditions = ['is'] as const;
export type StringColumnFilter = ColumnFilter & {
  type?: 'string';
  condition?: (typeof StringConditions)[number];
  columnName?: string;
  value?: { id: string; name?: string }[];
};

export type FilterType = DateRangeColumnFilter | DateColumnFilter | StringColumnFilter;
export type FilterGroup = {
  name: string;
  filters: FilterType[];
};

export interface InboxProps {
  sorting: SortingFilter[];
  filters: FilterType[];
  selectedRunIds: Set<string>;
  search: string | undefined;
  filterGroups: FilterGroup[];
  selectedFilterGroup: number;
  agentId?: string;
  isFetching?: boolean;
}

superjson.registerCustom<DateValue, string>(
  {
    isApplicable: (v): v is DateValue => typeof v === 'object' && 'calendar' in v,
    serialize: (v) => v.toString(),
    deserialize: (v) => parseDate(v),
  },
  'DateValue'
);

const storage: PersistStorage<InboxProps> = {
  getItem: (name) => {
    const str = localStorage.getItem(name);
    if (!str) return null;
    return superjson.parse(str);
  },
  setItem: (name, value) => {
    localStorage.setItem(name, superjson.stringify(value));
  },
  removeItem: (name) => localStorage.removeItem(name),
};

export type SortingUpdates = {
  [key: string]: Partial<SortingFilter>;
};

export interface InboxState extends InboxProps {
  setSorting: (sorting: SortingFilter[]) => void;
  updateSorting: (updates: SortingUpdates) => void;
  moveSorting: (fromIndex: number, toIndex: number) => void;
  addFilter: (filter?: Partial<FilterType>) => void;
  updateFilter: (id: string, updates: Partial<FilterType> | ((prev: FilterType) => FilterType)) => void;
  deleteFilter: (id: string) => void;
  clearFilters: () => void;
  addSelectedRunId: (id: string) => void;
  removeSelectedRunId: (id: string) => void;
  setSelectedRunIds: (selectedRunIds: string[] | Set<string>) => void;
  setSearch: (search: string | undefined) => void;
  setSelectedFilterGroup: (selectedFilterGroup: number) => void;
  setIsFetching: (isFetching: boolean) => void;
}

export const createInboxStore = (agentId?: string, initProps?: Partial<InboxProps>) => {
  const props: InboxProps = {
    sorting: [
      { id: 'createdTime', desc: true, hidden: false, name: 'Started' },
      { id: 'updatedTime', desc: true, hidden: true, name: 'Ended' },
    ],
    filters: [],
    filterGroups: [
      {
        name: 'All',
        filters: [],
      },
      {
        name: 'Needs review',
        filters: [
          {
            id: 'status',
            type: 'string',
            condition: 'is',
            value: [{ id: 'ready-for-review' }, { id: 'review-in-progress' }],
          },
        ],
      },
      {
        name: 'Completed',
        filters: [
          {
            id: 'status',
            type: 'string',
            condition: 'is',
            value: [{ id: 'completed' }],
          },
        ],
      },
    ],
    selectedFilterGroup: 0,
    search: undefined,
    selectedRunIds: new Set(),
    isFetching: false,
    agentId,
    ...initProps,
  };

  return createStore<InboxState>()(
    devtools(
      persist(
        immer((set) => ({
          ...props,
          setSorting: (sorting: SortingFilter[]) =>
            set((state) => {
              state.sorting = sorting;
            }),
          updateSorting: (updates: SortingUpdates) =>
            set((state) => {
              for (const [id, opts] of Object.entries(updates)) {
                const item = state.sorting.find((s) => s.id === id);
                if (item) {
                  Object.assign(item, opts);
                }
              }
            }),
          moveSorting: (fromIndex: number, toIndex: number) =>
            set((state) => {
              state.sorting = arrayMove(state.sorting, fromIndex, toIndex);
            }),
          addFilter: (filter?: Partial<FilterType>) =>
            set((state) => {
              state.filters.push({ id: generateId(), ...filter });
            }),
          updateFilter: (id: string, updates: Partial<FilterType> | ((prev: FilterType) => FilterType)) =>
            set((state) => {
              const filter = state.filters.find((f) => f.id === id);
              if (filter) {
                const v = updates instanceof Function ? updates(filter as FilterType) : updates;
                Object.assign(filter, v);
              }
            }),
          deleteFilter: (id: string) =>
            set((state) => {
              const index = state.filters.findIndex((f) => f.id === id);
              if (index > -1) {
                state.filters.splice(index, 1);
              }
            }),
          clearFilters: () =>
            set((state) => {
              state.filters = [];
            }),
          addSelectedRunId: (id: string) =>
            set((state) => {
              state.selectedRunIds.add(id);
            }),
          removeSelectedRunId: (id: string) =>
            set((state) => {
              state.selectedRunIds.delete(id);
            }),
          setSelectedRunIds: (selectedRunIds: string[] | Set<string>) =>
            set((state) => {
              state.selectedRunIds = new Set([...selectedRunIds]);
            }),
          setSearch: (search: string | undefined) =>
            set((state) => {
              state.search = search;
            }),
          setSelectedFilterGroup: (selectedFilterGroup: number) =>
            set((state) => {
              state.selectedRunIds?.clear();
              state.selectedFilterGroup = selectedFilterGroup;
            }),
          setIsFetching: (isFetching: boolean) =>
            set((state) => {
              state.isFetching = isFetching;
            }),
        })),
        { name: `${agentId}-${import.meta.env.VITE_CURRENT_COMMIT_SHA ?? ''}`, storage }
      )
    )
  );
};

export type InboxStore = ReturnType<typeof createInboxStore>;

export const InboxContext = createContext<InboxStore | null>(null);
